import { tva } from '@gluestack-ui/utils/nativewind-utils'
import React, { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import {
    CalendarGlyph,
    ChevronDownGlyph,
    ClockGlyph,
    GLYPH_COLORS,
} from '../picker-icons/pickerGlyphs'
import { Pressable } from '../pressable'
import { Text } from '../text'

export type PickerFieldIcon = 'chevron-down' | 'calendar' | 'clock' | 'none'
export type PickerFieldSize = 'md' | 'lg'

export type PickerFieldProps = {
    value?: ReactNode
    placeholder?: string
    icon?: PickerFieldIcon
    size?: PickerFieldSize
    isOpen?: boolean
    isInvalid?: boolean
    isDisabled?: boolean
    onPress?: () => void
    className?: string
    testID?: string
}

const pickerField = tva({
    base: 'flex-row items-center gap-2 overflow-hidden rounded border border-solid border-border-subtle bg-aos-bg-input px-3',
    variants: {
        size: {
            md: 'h-10',
            lg: 'h-12',
        },
        isOpen: {
            true: 'border-2 border-action-focus-ring px-[11px]',
            false: '',
        },
        isInvalid: {
            true: 'border-2 border-status-critical px-[11px]',
            false: '',
        },
        isDisabled: {
            true: 'pointer-events-none opacity-40',
            false: '',
        },
    },
    defaultVariants: {
        size: 'lg',
        isOpen: false,
        isInvalid: false,
        isDisabled: false,
    },
})

const glyphFor: Record<
    Exclude<PickerFieldIcon, 'none'>,
    React.FC<{ size?: number; color?: string }>
> = {
    'chevron-down': ChevronDownGlyph,
    'calendar': CalendarGlyph,
    'clock': ClockGlyph,
}

/**
 * Pressable read-only field that opens a picker (sheet, popover or native
 * dialog). Shares the Input chrome/tokens; the trailing glyph hints at the
 * picker kind. Controlled — the host owns the open state and the value.
 */
export const PickerField: React.FC<PickerFieldProps> = ({
    value,
    placeholder,
    icon = 'chevron-down',
    size = 'lg',
    isOpen = false,
    isInvalid = false,
    isDisabled = false,
    onPress,
    className,
    testID,
}) => {
    const Glyph = icon === 'none' ? null : glyphFor[icon]
    const hasValue = value !== undefined && value !== null && value !== ''

    return (
        <Pressable
            onPress={isDisabled ? undefined : onPress}
            disabled={isDisabled}
            accessibilityRole='button'
            accessibilityState={{ disabled: isDisabled, expanded: isOpen }}
            testID={testID}
            className={twMerge(pickerField({ size, isOpen, isInvalid, isDisabled }), className)}
        >
            <Box className='flex-1 flex-row items-center'>
                {hasValue ? (
                    typeof value === 'string' || typeof value === 'number' ? (
                        <Text size='body' tone='default' numberOfLines={1}>
                            {value}
                        </Text>
                    ) : (
                        value
                    )
                ) : (
                    <Text size='body' tone='muted' numberOfLines={1}>
                        {placeholder}
                    </Text>
                )}
            </Box>
            {Glyph ? <Glyph size={24} color={GLYPH_COLORS.default} /> : null}
        </Pressable>
    )
}

PickerField.displayName = 'PickerField'
